// utils/generalOptionsUtils.js

import { HitApi } from "../Api/ApiHit";
import { searchGeneralMaster } from "../constant/Constant";

/**
 * Fetch options from searchGeneralMaster API
 * @param {string} usedByValue - The usedBy value to filter options (e.g., 'Gender', 'Status')
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Number of items per page (default: 20)
 * @returns {Promise<Array>} - Array of formatted options
 */
export const fetchGeneralOptions = async (usedByValue, page = 1, limit = 20) => {
    try {
        const json = {
            page,
            limit,
            search: {
                "usedBy": usedByValue,
            }
        };

        const response = await HitApi(json, searchGeneralMaster);
        console.log(`Response for ${usedByValue}:`, response);

        // Transform API response to dropdown options format
        if (response.data.docs) {
            const transformedOptions = response.data.docs
                .filter(item => item.isActive) // Only include active items
                .map(item => ({
                    label: item.value, // Using 'value' field as label
                    value: item.value  // Using 'value' field as value
                }));

            console.log(`Transformed options for ${usedByValue}:`, transformedOptions);
            return transformedOptions;
        } else {
            console.log(`No data received for ${usedByValue}`);
            return [];
        }
    } catch (error) {
        console.error(`Error fetching ${usedByValue} options:`, error);
        return [];
    }
};

/**
 * Fetch multiple option types at once
 * @param {Array<string>} usedByValues - Array of usedBy values to fetch
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Number of items per page (default: 20)
 * @returns {Promise<Object>} - Object with keys as usedBy values and values as option arrays
 */
export const fetchMultipleGeneralOptions = async (usedByValues, page = 1, limit = 1000) => {
    try {
        const promises = usedByValues.map(usedBy => 
            fetchGeneralOptions(usedBy, page, limit)
        );

        const results = await Promise.all(promises);
        
        // Create object with usedBy as key and options as value
        const optionsObject = {};
        usedByValues.forEach((usedBy, index) => {
            optionsObject[usedBy] = results[index];
        });

        return optionsObject;
    } catch (error) {
        console.error('Error fetching multiple options:', error);
        return {};
    }
};

/**
 * Hook for using general options in React components
 * @param {string|Array<string>} usedByValue - Single usedBy value or array of values
 * @param {Object} options - Additional options
 * @returns {Object} - State and functions for managing options
 */
export const useGeneralOptions = (usedByValue, options = {}) => {
    const { page = 1, limit = 1000, fallbackOptions = {} } = options;
    
    // This would typically use React hooks, but since this is a utility file,
    // we'll provide the base functions. The actual hook implementation would be in a separate file.
    
    const loadOptions = async () => {
        if (Array.isArray(usedByValue)) {
            return await fetchMultipleGeneralOptions(usedByValue, page, limit);
        } else {
            const options = await fetchGeneralOptions(usedByValue, page, limit);
            return { [usedByValue]: options };
        }
    };

    return {
        loadOptions,
        fetchGeneralOptions,
        fetchMultipleGeneralOptions
    };
};

// Predefined option fetchers for common use cases
export const fetchGenderOptions = () => fetchGeneralOptions('Gender');
export const fetchStatusOptions = () => fetchGeneralOptions('Status');
export const fetchseatStatusOptions = () => fetchGeneralOptions('SeatStatus');

// export const fetchCategoryOptions = () => fetchGeneralOptions('Category');
// export const fetchTypeOptions = () => fetchGeneralOptions('Type');

// Helper function to get options with fallback
export const getOptionsWithFallback = (apiOptions, fallbackOptions) => {

    console.log("apiOptions",apiOptions);
    console.log("fallbackOptions",fallbackOptions);
    
    
    return apiOptions && apiOptions.length > 0 ? apiOptions : fallbackOptions;
};